/**
 * i18nLoader — the platform's translation bundles, and the ONE place that
 * decides what is bundled and what is loaded on demand.
 *
 * ── The rule ───────────────────────────────────────────────────────────────
 * ENGLISH IS BUNDLED. EVERY OTHER LANGUAGE IS A LAZY CHUNK, PER NAMESPACE.
 *
 * English is the fallback for every miss. Polyglot's `translate()` returns THE
 * KEY ITSELF when no lookup step resolves, so a base language that needed a
 * round trip would put a raw `common.action.save` on screen the first time a
 * network hiccuped. It ships inside the bundle and touches nothing.
 *
 * Every other language is `() => import('../i18n/<lang>/<ns>.json')` — a
 * static specifier inside a thunk. Parcel code-splits each one into its own
 * content-hashed chunk, so a visitor who never switches language downloads not
 * one byte of it, and a visitor who does downloads exactly the namespaces the
 * running surface declared.
 *
 * ── Why this shape and not the two obvious alternatives ────────────────────
 * Measured on Parcel 2.16.4 with a real build (the bundler every app in this
 * repo runs; evidence recorded on LANE POLYGLOT-ARCH, 2026-09-02):
 *
 *  1. BUNDLE EVERY LANGUAGE (what this file used to do). Correct at 77 keys,
 *     wrong at the platform's target size: English alone is ~100 KB at ~2900
 *     keys and each further language adds ~120 KB to the FIRST paint of every
 *     visitor, in a language almost none of them will pick. A build proved the
 *     failure mode is invisible to a functional test: with every language
 *     statically imported a switch still returns Georgian, so a test that only
 *     asserts "the switch works" passes while nothing is lazy.
 *
 *  2. A VARIABLE-PATH DYNAMIC IMPORT — import with a template-literal path.
 *     Parcel emits NO chunk, NO importmap entry and NO WARNING; it copies the
 *     template literal into the bundle verbatim. The browser then resolves it
 *     against the bundle URL, i.e. `/i18n/ka/common.json` — and the dev server
 *     answers an unknown path with 200 + SPA-fallback HTML, which `res.ok`
 *     happily accepts. That is the same silent 200-that-is-not-JSON this file
 *     was already burned by once (see "the fetch path that never worked"
 *     below); a template literal walks straight back into it.
 *
 *  3. A STATIC-SPECIFIER THUNK MAP — what is generated below. Parcel emits one
 *     content-hashed chunk per entry plus an importmap entry. Booting in
 *     English loads ZERO chunks; a switch to ka/common loads exactly one.
 *     There is no runtime path to 404 and no content type to sniff, because
 *     the bundler resolved the specifier at build time.
 *
 * ── The fetch path that never worked, and is gone ──────────────────────────
 * This file used to fetch `/i18n/<lang>/<ns>.json` at runtime. Measured live on
 * the running shell: `new URL('../i18n/ka/common.json', import.meta.url)`
 * resolved to `/common.f873341d.js` — a JAVASCRIPT MODULE, because Parcel runs
 * its JSON transformer on a `.json` asset. `res.json()` threw on 31 KB of JS.
 * The literal runtime path was worse: 200, 30 KB of SPA-fallback HTML, and only
 * the parse error to tell you. Nothing on the load path touches the network any
 * more, so neither trap can return.
 *
 * ── The three planes, and what each one gets ───────────────────────────────
 *  • BUNDLER plane (local dev, and every deployed app build): English static,
 *    everything else a chunk. This is the plane the design targets.
 *  • PUBLISHED plane (a frank record — module bodies never run, the code is
 *    revived from JSON): `context.polyglot.translations` is serialized DATA, so
 *    whatever `i18nStaticTranslations()` returned when frank ran its Node pass
 *    is baked into the payload. English therefore works with no module body at
 *    all. A language with no baked bundle stays English and says so once — a
 *    published record has no chunk to import.
 *  • NODE plane (frank's own build pass, and the bun test runner): module
 *    bodies run, so both registries are real; `with { type: 'json' }` is what
 *    makes the specifiers legal there (Node 24 throws
 *    ERR_IMPORT_ATTRIBUTE_MISSING without it, Bun and Parcel both accept it).
 *
 * ── Adding a language or a namespace ───────────────────────────────────────
 * Drop the file in `packages/shared/i18n/<lang>/<namespace>.json` and run
 * `node scripts/gen-i18n-registry.mjs`. Nothing else. The generated block
 * below is the single writer of the registry, `__tests__/i18nRegistry.test.js`
 * fails when it drifts from disk, and `NAMESPACES` — which bootShell
 * intersects app manifests against — is derived from the same scan, so a
 * manifest's `i18nNamespaces: ['canvas']` goes live the moment
 * `i18n/en/canvas.json` exists.
 *
 * Key naming, and what belongs in `common` versus a surface namespace:
 * `docs/I18N-CONVENTION.md`. Read it before you name a key.
 *
 * ── FA204 / frank section-barrel trap ──────────────────────────────────────
 * `functions/` is a frank SECTION BARREL: every export becomes its own emitted
 * entry, and a module-scope binding read from inside an exported function is
 * unresolvable in the revived runtime. So the registries and the cross-call
 * cache are stashed on `(window||globalThis)` by the module body, and every
 * export below carries its own self-contained lazy read of that holder rather
 * than sharing a module-scope helper (which would be the same trap one level
 * down). The stash is try-guarded because frank runs this module in Node while
 * building `globalScope`; a throw there would take the whole package's
 * emission down.
 */

// ─── i18n:registry BEGIN — generated by scripts/gen-i18n-registry.mjs, do not hand-edit ───
// The BASE language is bundled: it is the fallback for every miss, and
// polyglot's translate() returns the KEY ITSELF when no step resolves — so a
// base that needed a round trip would put `common.action.save` on screen.
import enAccount from '../i18n/en/account.json' with { type: 'json' }
import enAdmin from '../i18n/en/admin.json' with { type: 'json' }
import enAi from '../i18n/en/ai.json' with { type: 'json' }
import enAnalytics from '../i18n/en/analytics.json' with { type: 'json' }
import enApps from '../i18n/en/apps.json' with { type: 'json' }
import enAuth from '../i18n/en/auth.json' with { type: 'json' }
import enCalendar from '../i18n/en/calendar.json' with { type: 'json' }
import enCanvas from '../i18n/en/canvas.json' with { type: 'json' }
import enChat from '../i18n/en/chat.json' with { type: 'json' }
import enCommon from '../i18n/en/common.json' with { type: 'json' }
import enData from '../i18n/en/data.json' with { type: 'json' }
import enDocuments from '../i18n/en/documents.json' with { type: 'json' }
import enEditor from '../i18n/en/editor.json' with { type: 'json' }
import enFiles from '../i18n/en/files.json' with { type: 'json' }
import enFlags from '../i18n/en/flags.json' with { type: 'json' }
import enInfra from '../i18n/en/infra.json' with { type: 'json' }
import enLearn from '../i18n/en/learn.json' with { type: 'json' }
import enMail from '../i18n/en/mail.json' with { type: 'json' }
import enMarketplace from '../i18n/en/marketplace.json' with { type: 'json' }
import enMeet from '../i18n/en/meet.json' with { type: 'json' }
import enMembers from '../i18n/en/members.json' with { type: 'json' }
import enPreview from '../i18n/en/preview.json' with { type: 'json' }
import enPublish from '../i18n/en/publish.json' with { type: 'json' }
import enSettings from '../i18n/en/settings.json' with { type: 'json' }
import enShell from '../i18n/en/shell.json' with { type: 'json' }
import enTickets from '../i18n/en/tickets.json' with { type: 'json' }
import enWorkspace from '../i18n/en/workspace.json' with { type: 'json' }

const BASE_BUNDLES = {
  'account': enAccount,
  'admin': enAdmin,
  'ai': enAi,
  'analytics': enAnalytics,
  'apps': enApps,
  'auth': enAuth,
  'calendar': enCalendar,
  'canvas': enCanvas,
  'chat': enChat,
  'common': enCommon,
  'data': enData,
  'documents': enDocuments,
  'editor': enEditor,
  'files': enFiles,
  'flags': enFlags,
  'infra': enInfra,
  'learn': enLearn,
  'mail': enMail,
  'marketplace': enMarketplace,
  'meet': enMeet,
  'members': enMembers,
  'preview': enPreview,
  'publish': enPublish,
  'settings': enSettings,
  'shell': enShell,
  'tickets': enTickets,
  'workspace': enWorkspace,
}

// Every other language, one lazily-fetched content-hashed chunk per
// namespace. Static specifiers on purpose — a template literal produces no
// chunk at all under Parcel (see scripts/gen-i18n-registry.mjs header).
const LAZY_BUNDLES = {
  'ka/account': () => import('../i18n/ka/account.json', { with: { type: 'json' } }),
  'ka/admin': () => import('../i18n/ka/admin.json', { with: { type: 'json' } }),
  'ka/ai': () => import('../i18n/ka/ai.json', { with: { type: 'json' } }),
  'ka/analytics': () => import('../i18n/ka/analytics.json', { with: { type: 'json' } }),
  'ka/apps': () => import('../i18n/ka/apps.json', { with: { type: 'json' } }),
  'ka/auth': () => import('../i18n/ka/auth.json', { with: { type: 'json' } }),
  'ka/calendar': () => import('../i18n/ka/calendar.json', { with: { type: 'json' } }),
  'ka/canvas': () => import('../i18n/ka/canvas.json', { with: { type: 'json' } }),
  'ka/chat': () => import('../i18n/ka/chat.json', { with: { type: 'json' } }),
  'ka/common': () => import('../i18n/ka/common.json', { with: { type: 'json' } }),
  'ka/data': () => import('../i18n/ka/data.json', { with: { type: 'json' } }),
  'ka/documents': () => import('../i18n/ka/documents.json', { with: { type: 'json' } }),
  'ka/editor': () => import('../i18n/ka/editor.json', { with: { type: 'json' } }),
  'ka/files': () => import('../i18n/ka/files.json', { with: { type: 'json' } }),
  'ka/flags': () => import('../i18n/ka/flags.json', { with: { type: 'json' } }),
  'ka/infra': () => import('../i18n/ka/infra.json', { with: { type: 'json' } }),
  'ka/learn': () => import('../i18n/ka/learn.json', { with: { type: 'json' } }),
  'ka/mail': () => import('../i18n/ka/mail.json', { with: { type: 'json' } }),
  'ka/marketplace': () => import('../i18n/ka/marketplace.json', { with: { type: 'json' } }),
  'ka/meet': () => import('../i18n/ka/meet.json', { with: { type: 'json' } }),
  'ka/members': () => import('../i18n/ka/members.json', { with: { type: 'json' } }),
  'ka/preview': () => import('../i18n/ka/preview.json', { with: { type: 'json' } }),
  'ka/publish': () => import('../i18n/ka/publish.json', { with: { type: 'json' } }),
  'ka/settings': () => import('../i18n/ka/settings.json', { with: { type: 'json' } }),
  'ka/shell': () => import('../i18n/ka/shell.json', { with: { type: 'json' } }),
  'ka/tickets': () => import('../i18n/ka/tickets.json', { with: { type: 'json' } }),
  'ka/workspace': () => import('../i18n/ka/workspace.json', { with: { type: 'json' } }),
}

const BASE_LANG = 'en'
const LANGS = ['en', 'ka']
const NAMESPACES = ['account', 'admin', 'ai', 'analytics', 'apps', 'auth', 'calendar', 'canvas', 'chat', 'common', 'data', 'documents', 'editor', 'files', 'flags', 'infra', 'learn', 'mail', 'marketplace', 'meet', 'members', 'preview', 'publish', 'settings', 'shell', 'tickets', 'workspace']
// ─── i18n:registry END ───

// The module body's one job: hand both registries and an empty cache to the
// holder every export reads. See the FA204 note in the header.
try {
  const w = typeof window !== 'undefined' ? window : globalThis
  w.__i18nRegistry = {
    baseLang: BASE_LANG,
    base: BASE_BUNDLES,
    lazy: LAZY_BUNDLES,
    langs: LANGS,
    namespaces: NAMESPACES
  }
  if (!w.__i18nLoaderState) {
    w.__i18nLoaderState = {
      // 'lang/ns' -> resolved bundle object
      cache: new Map(),
      // namespace names this session has asked for, in any language. setLang
      // re-loads exactly this set into the target language.
      loadedNamespaces: new Set(),
      // 'lang/ns' -> in-flight promise, so N elements asking at once produce
      // ONE chunk request.
      inflight: new Map(),
      // languages we have already told the console about, so a published
      // record with no chunks warns once, not once per namespace.
      warned: new Set()
    }
  }
} catch (_) {
  // No module context (frank's revived runtime). Every export below degrades
  // to the base language, which the payload already carries.
}

/**
 * The BUNDLED translations — what `context.polyglot.translations` is seeded
 * with. English only, by design: see the header.
 *
 * Returns `{}` when no module body ran (frank's revived runtime); the published
 * payload carries the serialized copy in that case.
 *
 * @returns {{ [lang: string]: object }} e.g. `{ en: { 'common.action.save': 'Save' } }`
 */
export const i18nStaticTranslations = () => {
  const w = typeof window !== 'undefined' ? window : globalThis
  // Read BOTH holders. In a browser `window === globalThis`, so this is one
  // lookup — but a non-browser host (bun/node test runs, SSR) can acquire a
  // `window` shim AFTER this module's body ran, which would leave the registry
  // on `globalThis` while every later read looked at `window` and found
  // nothing. Returning `{}` there would silently make English absent.
  const reg = w.__i18nRegistry || globalThis.__i18nRegistry
  if (!reg) return {}
  const merged = {}
  for (const ns of Object.keys(reg.base)) Object.assign(merged, reg.base[ns])
  return { [reg.baseLang]: merged }
}

/**
 * The bundled English base alone — `translations.en`, and the fallback every
 * other language degrades to. Polyglot's `translate()` returns THE KEY ITSELF
 * when no lookup step resolves, so a missing English base is what puts a raw
 * `common.action.save` on screen.
 *
 * @returns {object}
 */
export const i18nStaticEnglish = () => {
  const w = typeof window !== 'undefined' ? window : globalThis
  const reg = w.__i18nRegistry || globalThis.__i18nRegistry
  if (!reg) return {}
  const merged = {}
  for (const ns of Object.keys(reg.base)) Object.assign(merged, reg.base[ns])
  return merged
}

/**
 * Is there a bundle on disk for this pair? Lets a caller decide BEFORE it
 * awaits — bootShell uses it to skip a language it cannot serve rather than
 * awaiting a load that can only fall back.
 *
 * @param {string} lang
 * @param {string} namespace
 * @returns {boolean}
 */
export const hasI18nBundle = (lang, namespace) => {
  const w = typeof window !== 'undefined' ? window : globalThis
  const reg = w.__i18nRegistry || globalThis.__i18nRegistry
  if (!reg) return false
  if (lang === reg.baseLang) return !!reg.base[namespace]
  return !!reg.lazy[`${lang}/${namespace}`]
}

/**
 * Load ONE namespace for ONE language. No network, on any plane.
 *
 * Resolution order:
 *   1. the base language -> the statically bundled object, synchronously ready
 *   2. a cached chunk from an earlier call
 *   3. the registry thunk -> `await import()` -> its own content-hashed chunk
 *   4. nothing -> the base-language bundle, and one warning per language
 *
 * Step 4 is a real outcome, not an error path: a frank-published record has no
 * chunks to import. It degrades to English rather than to a raw key, and says
 * which language it could not serve.
 *
 * Concurrent callers for the same pair share ONE import — N elements asking at
 * once must not produce N chunk requests.
 *
 * @param {string} lang       - ISO language code
 * @param {string} namespace  - bundle name (e.g. 'common', 'preview')
 * @returns {Promise<object>} the bundle, or the base-language bundle on a miss
 */
export const loadI18nNamespace = async (lang, namespace) => {
  const w = typeof window !== 'undefined' ? window : globalThis
  const reg = w.__i18nRegistry || globalThis.__i18nRegistry
  // Normalize the shape rather than trusting it. The state lives on a GLOBAL,
  // so a warm page, an older bundle or a test harness can hand us an object
  // that predates one of these fields — and reading `.has` off an undefined
  // Map is a hard TypeError in the middle of a language switch.
  const _st = w.__i18nLoaderState || (w.__i18nLoaderState = {})
  if (!_st.cache) _st.cache = new Map()
  if (!_st.loadedNamespaces) _st.loadedNamespaces = new Set()
  if (!_st.inflight) _st.inflight = new Map()
  if (!_st.warned) _st.warned = new Set()
  const state = _st
  if (!reg) return {}

  const key = `${lang}/${namespace}`
  state.loadedNamespaces.add(namespace)

  if (lang === reg.baseLang) return reg.base[namespace] || {}
  if (state.cache.has(key)) return state.cache.get(key)
  if (state.inflight.has(key)) return state.inflight.get(key)

  const thunk = reg.lazy[key]
  if (!thunk) {
    if (!state.warned.has(lang)) {
      state.warned.add(lang)
      console.warn(
        `[i18n] no bundle for '${lang}' — falling back to '${reg.baseLang}'. Add ` +
          `packages/shared/i18n/${lang}/${namespace}.json and run ` +
          'scripts/gen-i18n-registry.mjs, ' +
          'or (on a published record) bake the language into context.polyglot.translations.'
      )
    }
    return reg.base[namespace] || {}
  }

  const p = thunk()
    .then((mod) => {
      // Parcel/Node/Bun all hand back a module namespace; the JSON object is
      // its default export. `mod` itself is the object under some CJS interop
      // shapes, so accept both (FA206's defensive-destructure rule).
      const data = mod?.default || mod
      // A bundle that arrives as anything but a plain object is a build fault,
      // not a translation. Refuse it rather than caching a module namespace
      // and rendering "[object Module]" into the UI.
      if (!data || typeof data !== 'object' || Array.isArray(data)) {
        console.warn(`[i18n] '${key}' did not resolve to an object — using '${reg.baseLang}'`)
        return reg.base[namespace] || {}
      }
      state.cache.set(key, data)
      return data
    })
    .catch((err) => {
      console.warn(
        `[i18n] '${key}' failed to load (${err?.message || err}) — using '${reg.baseLang}'`
      )
      return reg.base[namespace] || {}
    })
    .finally(() => {
      state.inflight.delete(key)
    })

  state.inflight.set(key, p)
  return p
}

/**
 * Load a SET of namespaces for one language and return them merged, in
 * declaration order, so a later namespace can intentionally override an
 * earlier one. The chunks load in PARALLEL — a switch on a surface with five
 * namespaces is one round of requests, not five serial ones.
 *
 * @param {string} lang
 * @param {string[]} namespaces
 * @returns {Promise<object>}
 */
export const loadI18nNamespaces = async (lang, namespaces) => {
  const list = Array.from(new Set(namespaces || []))
  const parts = await Promise.all(list.map((ns) => loadI18nNamespace(lang, ns)))
  const merged = {}
  for (const part of parts) Object.assign(merged, part)
  return merged
}

/**
 * Evict every cached non-base bundle. Called before re-loading in a new
 * language so a stale chunk cannot answer for the wrong one.
 */
export const dropNonEnglishCache = () => {
  const w = typeof window !== 'undefined' ? window : globalThis
  const reg = w.__i18nRegistry || globalThis.__i18nRegistry
  const base = reg?.baseLang || 'en'
  // Normalize the shape rather than trusting it. The state lives on a GLOBAL,
  // so a warm page, an older bundle or a test harness can hand us an object
  // that predates one of these fields — and reading `.has` off an undefined
  // Map is a hard TypeError in the middle of a language switch.
  const _st = w.__i18nLoaderState || (w.__i18nLoaderState = {})
  if (!_st.cache) _st.cache = new Map()
  if (!_st.loadedNamespaces) _st.loadedNamespaces = new Set()
  if (!_st.inflight) _st.inflight = new Map()
  if (!_st.warned) _st.warned = new Set()
  for (const k of Array.from(_st.cache.keys())) {
    if (!k.startsWith(`${base}/`)) _st.cache.delete(k)
  }
}

/**
 * Every namespace this session has asked for, in any language. This is the set
 * a language switch must re-load — NOT the set that happens to be cached,
 * which for the base language is everything and for a chunked language is
 * whatever loaded last.
 *
 * @returns {string[]}
 */
export const getLoadedNamespaces = () => {
  const w = typeof window !== 'undefined' ? window : globalThis
  // Normalize the shape rather than trusting it. The state lives on a GLOBAL,
  // so a warm page, an older bundle or a test harness can hand us an object
  // that predates one of these fields — and reading `.has` off an undefined
  // Map is a hard TypeError in the middle of a language switch.
  const _st = w.__i18nLoaderState || (w.__i18nLoaderState = {})
  if (!_st.cache) _st.cache = new Map()
  if (!_st.loadedNamespaces) _st.loadedNamespaces = new Set()
  if (!_st.inflight) _st.inflight = new Map()
  if (!_st.warned) _st.warned = new Set()
  return Array.from(_st.loadedNamespaces)
}

/**
 * Merge every namespace already resolved for `lang` into one object. Falls
 * back to the base-language bundle for any namespace not resolved for `lang`,
 * so a partial load degrades to English key by key and never to a raw key.
 *
 * Synchronous and cache-only on purpose: it is the "what do we have right now"
 * read. Use `loadI18nNamespaces` when you need the chunks fetched.
 *
 * @param {string} lang
 * @returns {object}
 */
export const mergeAllNamespaces = (lang) => {
  const w = typeof window !== 'undefined' ? window : globalThis
  const reg = w.__i18nRegistry || globalThis.__i18nRegistry
  // Normalize the shape rather than trusting it. The state lives on a GLOBAL,
  // so a warm page, an older bundle or a test harness can hand us an object
  // that predates one of these fields — and reading `.has` off an undefined
  // Map is a hard TypeError in the middle of a language switch.
  const _st = w.__i18nLoaderState || (w.__i18nLoaderState = {})
  if (!_st.cache) _st.cache = new Map()
  if (!_st.loadedNamespaces) _st.loadedNamespaces = new Set()
  if (!_st.inflight) _st.inflight = new Map()
  if (!_st.warned) _st.warned = new Set()
  const state = _st
  const base = reg?.baseLang || 'en'
  const merged = {}
  for (const ns of state.loadedNamespaces) {
    if (lang === base) {
      Object.assign(merged, reg?.base?.[ns] || {})
      continue
    }
    Object.assign(merged, state.cache.get(`${lang}/${ns}`) || reg?.base?.[ns] || {})
  }
  return merged
}

export { LANGS, NAMESPACES, BASE_LANG }
